import React, { useState, useEffect } from 'react';
import apiClient from '../../utils/api';
import { toast } from 'sonner';
import ConfirmModal from './ConfirmModal';
import './OrderDashboard.css';

function OrderDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    await Promise.all([fetchAnalytics(), fetchOrders()]);
  };

  const fetchAnalytics = async () => {
    try {
      const data = await apiClient.getDashboardAnalytics();
      setAnalytics(data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const fetchOrders = async () => {
    try {
      const data = await apiClient.getOrders(100);
      setOrders(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await apiClient.updateOrderStatus(orderId, newStatus);
      toast.success('Status erfolgreich aktualisiert!');
      fetchOrders();
      fetchAnalytics(); // Refresh analytics
    } catch (error) {
      toast.error('Fehler beim Aktualisieren: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleBankTransferConfirm = async (orderId) => {
    setConfirmModal({
      isOpen: true,
      title: 'Banküberweisung bestätigen',
      message: 'Banküberweisung als bezahlt markieren?',
      onConfirm: async () => {
        try {
          await apiClient.confirmBankTransfer(orderId, 'Vom Admin bestätigt');
          toast.success('Banküberweisung erfolgreich bestätigt!');
          await fetchOrders();
          await fetchAnalytics();
        } catch (error) {
          toast.error('Fehler: ' + (error.response?.data?.detail || error.message));
        } finally {
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });
        }
      }
    });
  };

  const handleManualCoinCredit = async (orderId, orderNumber, totalCoins) => {
    console.log('🔵 handleManualCoinCredit called:', { orderId, orderNumber, totalCoins });
    
    setConfirmModal({
      isOpen: true,
      title: 'Coins gutschreiben',
      message: `Möchtest du ${totalCoins} Coins für Bestellung ${orderNumber} manuell gutschreiben?\n\nDies sollte nur gemacht werden, wenn die PayPal-Zahlung erfolgreich war, aber die Coins nicht automatisch gutgeschrieben wurden.`,
      onConfirm: async () => {
        try {
          console.log('📤 Sending API request to credit coins...');
          const result = await apiClient.manuallyCreditCoins(orderId);
          console.log('✅ API response:', result);
          
          toast.success(`✅ ${result.coins_credited} Coins erfolgreich gutgeschrieben!`);
          
          console.log('🔄 Refreshing orders and analytics...');
          await fetchOrders();
          await fetchAnalytics();
          console.log('✅ Refresh complete');
        } catch (error) {
          console.error('❌ Error crediting coins:', error);
          toast.error('Fehler: ' + (error.message || 'Unbekannter Fehler'));
        } finally {
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });
        }
      }
    });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'Ausstehend', color: 'orange' },
      paid: { label: 'Bezahlt', color: 'green' },
      completed: { label: 'Abgeschlossen', color: 'blue' },
      cancelled: { label: 'Storniert', color: 'gray' },
      refunded: { label: 'Erstattet', color: 'purple' },
      failed: { label: 'Fehlgeschlagen', color: 'red' },
      expired: { label: 'Abgelaufen', color: 'gray' },
      paused: { label: 'Pausiert', color: 'yellow' }
    };
    const config = statusMap[status] || { label: status, color: 'gray' };
    return <span className={`status-badge ${config.color}`}>{config.label}</span>;
  };

  const filteredOrders = statusFilter === 'all' 
    ? orders 
    : orders.filter(o => o.status === statusFilter);

  if (loading) {
    return <div className="order-list-loading">Lade Bestellungen...</div>;
  }

  return (
    <div className="order-list" data-testid="order-list">
      <div className="order-list-header">
        <h2>📦 Bestellungen ({filteredOrders.length})</h2>
        <div className="order-filters">
          <select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
            data-testid="status-filter"
          >
            <option value="all">Alle Status</option>
            <option value="pending">Ausstehend</option>
            <option value="paid">Bezahlt</option>
            <option value="completed">Abgeschlossen</option>
            <option value="cancelled">Storniert</option>
          </select>
          <button onClick={fetchOrders} className="btn-refresh-small">
            🔄
          </button>
        </div>
      </div>

      {filteredOrders.length === 0 ? (
        <div className="no-orders">
          <p>Keine Bestellungen gefunden</p>
        </div>
      ) : (
        <div className="orders-table-container">
          <table className="orders-table">
            <thead>
              <tr>
                <th>Bestellnummer</th>
                <th>User</th>
                <th>Typ</th>
                <th>Produkt</th>
                <th>Betrag</th>
                <th>Zahlungsmethode</th>
                <th>Status</th>
                <th>Datum</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map(order => (
                <tr key={order.id} onClick={() => setSelectedOrder(order)} data-testid={`order-row-${order.id}`}>
                  <td className="order-number">{order.order_number}</td>
                  <td>{order.user_email}</td>
                  <td>
                    {order.order_type === 'subscription' ? '🔄 Abo' : 
                     order.order_type === 'coins' ? '🪙 Coins' : '💎 Einmal'}
                  </td>
                  <td>{order.items[0]?.product_name}</td>
                  <td className="amount">{order.total_amount}€</td>
                  <td>
                    {order.payment_method === 'paypal' ? '💙 PayPal' : '🏦 Bank'}
                  </td>
                  <td>{getStatusBadge(order.status)}</td>
                  <td>{new Date(order.created_at).toLocaleDateString('de-DE')}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {/* Bank Transfer Confirm Button */}
                    {order.status === 'pending' && order.payment_method === 'bank_transfer' && order.order_type !== 'coins' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleBankTransferConfirm(order.id);
                        }}
                        className="btn-confirm"
                        data-testid={`confirm-order-${order.id}`}
                      >
                        ✅ Bestätigen
                      </button>
                    )}
                    
                    {/* Coin Credit Button for pending/paid coin orders */}
                    {order.order_type === 'coins' && (order.status === 'pending' || order.status === 'paid') && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const item = order.items && order.items[0];
                          const totalCoins = item ? ((item.coins || 0) + (item.bonus_coins || 0)) : 0;
                          console.log('Coin credit button clicked:', { orderId: order.id, item, totalCoins });
                          handleManualCoinCredit(order.id, order.order_number, totalCoins);
                        }}
                        className="btn-credit-coins"
                        data-testid={`credit-coins-${order.id}`}
                        title="Coins manuell gutschreiben"
                      >
                        🪙 Coins gutschreiben
                      </button>
                    )}
                    
                    {/* Coins Credited Status for completed coin orders */}
                    {order.order_type === 'coins' && order.status === 'completed' && (
                      <span className="coins-credited-status" data-testid={`coins-credited-${order.id}`}>
                        ✅ Coins gutgeschrieben
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="order-modal" onClick={() => setSelectedOrder(null)}>
          <div className="order-modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Bestellung {selectedOrder.order_number}</h2>
              <button onClick={() => setSelectedOrder(null)} className="btn-close">×</button>
            </div>
            
            <div className="modal-body">
              <div className="detail-columns">
                {/* Left Column */}
                <div className="detail-column">
                  <div className="detail-section">
                    <h3>👤 Kundeninformationen</h3>
                    <p><strong>E-Mail:</strong> {selectedOrder.user_email}</p>
                    <p><strong>User ID:</strong> {selectedOrder.user_id}</p>
                  </div>

                  <div className="detail-section">
                    <h3>📦 Bestelldetails</h3>
                    <p><strong>Bestellnummer:</strong> {selectedOrder.order_number}</p>
                    <p><strong>Typ:</strong> 
                      {selectedOrder.order_type === 'subscription' ? '🔄 Abonnement' : 
                       selectedOrder.order_type === 'coins' ? '🪙 Coin-Kauf' : '💎 Einmalkauf'}
                    </p>
                    <p><strong>Status:</strong> {getStatusBadge(selectedOrder.status)}</p>
                    <p><strong>Erstellt:</strong> {new Date(selectedOrder.created_at).toLocaleString('de-DE')}</p>
                    {selectedOrder.paid_at && (
                      <p><strong>Bezahlt:</strong> {new Date(selectedOrder.paid_at).toLocaleString('de-DE')}</p>
                    )}
                    {selectedOrder.completed_at && (
                      <p><strong>Abgeschlossen:</strong> {new Date(selectedOrder.completed_at).toLocaleString('de-DE')}</p>
                    )}
                  </div>

                  <div className="detail-section">
                    <h3>🛒 Produkte</h3>
                    {selectedOrder.items.map((item, idx) => (
                      <div key={idx} className="item-row">
                        <span className="item-name">{item.product_name}</span>
                        <span className="item-qty">{item.quantity}x</span>
                        <span className="item-price">{item.total_price}€</span>
                      </div>
                    ))}
                  </div>

                  <div className="detail-section">
                    <h3>💳 Zahlung</h3>
                    <p><strong>Methode:</strong> {selectedOrder.payment_method === 'paypal' ? '💙 PayPal' : '🏦 Banküberweisung'}</p>
                    <p><strong>Zwischensumme:</strong> {selectedOrder.subtotal}€</p>
                    {selectedOrder.discount > 0 && (
                      <p><strong>Rabatt:</strong> -{selectedOrder.discount}€</p>
                    )}
                    <p className="total-row"><strong>Gesamt:</strong> <span className="total-amount">{selectedOrder.total_amount}€</span></p>
                  </div>
                </div>

                {/* Right Column - Addresses & Notes */}
                <div className="detail-column">
                  {/* Billing Address */}
                  {selectedOrder.billing_address && (
                    <div className="detail-section address-section">
                      <h3>📋 Rechnungsadresse</h3>
                      <div className="address-display">
                        <p className="address-name">
                          {selectedOrder.billing_address.first_name} {selectedOrder.billing_address.last_name}
                        </p>
                        {selectedOrder.billing_address.company && (
                          <p className="address-company">{selectedOrder.billing_address.company}</p>
                        )}
                        <p>
                          {selectedOrder.billing_address.street} {selectedOrder.billing_address.street_number}
                        </p>
                        {selectedOrder.billing_address.additional_info && (
                          <p className="address-additional">{selectedOrder.billing_address.additional_info}</p>
                        )}
                        <p>
                          {selectedOrder.billing_address.postal_code} {selectedOrder.billing_address.city}
                        </p>
                        <p>{selectedOrder.billing_address.country}</p>
                        {selectedOrder.billing_address.phone && (
                          <p className="address-phone">📞 {selectedOrder.billing_address.phone}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Shipping Address */}
                  {selectedOrder.shipping_address && (
                    <div className="detail-section address-section shipping">
                      <h3>📦 Lieferadresse</h3>
                      <div className="address-display">
                        <p className="address-name">
                          {selectedOrder.shipping_address.first_name} {selectedOrder.shipping_address.last_name}
                        </p>
                        {selectedOrder.shipping_address.company && (
                          <p className="address-company">{selectedOrder.shipping_address.company}</p>
                        )}
                        <p>
                          {selectedOrder.shipping_address.street} {selectedOrder.shipping_address.street_number}
                        </p>
                        {selectedOrder.shipping_address.additional_info && (
                          <p className="address-additional">{selectedOrder.shipping_address.additional_info}</p>
                        )}
                        <p>
                          {selectedOrder.shipping_address.postal_code} {selectedOrder.shipping_address.city}
                        </p>
                        <p>{selectedOrder.shipping_address.country}</p>
                      </div>
                    </div>
                  )}

                  {/* No Address Warning for physical products */}
                  {!selectedOrder.billing_address && selectedOrder.order_type !== 'coins' && selectedOrder.order_type !== 'subscription' && (
                    <div className="detail-section warning-section">
                      <h3>⚠️ Achtung</h3>
                      <p>Keine Adressdaten vorhanden! Bitte den Kunden kontaktieren.</p>
                    </div>
                  )}

                  {/* Customer Notes */}
                  {selectedOrder.customer_notes && (
                    <div className="detail-section notes-section customer-notes">
                      <h3>📝 Kundennotizen</h3>
                      <div className="notes-content">
                        {selectedOrder.customer_notes}
                      </div>
                    </div>
                  )}

                  {/* Admin Notes */}
                  {selectedOrder.admin_notes && (
                    <div className="detail-section notes-section admin-notes">
                      <h3>🔒 Admin-Notizen</h3>
                      <div className="notes-content">
                        {selectedOrder.admin_notes}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Section */}
              <div className="detail-section action-section">
                <h3>⚙️ Aktionen</h3>
                <div className="action-row">
                  <label>Status ändern:</label>
                  <select
                    onChange={(e) => handleStatusChange(selectedOrder.id, e.target.value)}
                    defaultValue={selectedOrder.status}
                    className="status-select"
                  >
                    <option value="pending">⏳ Ausstehend</option>
                    <option value="paid">✅ Bezahlt</option>
                    <option value="processing">🔄 In Bearbeitung</option>
                    <option value="shipped">📦 Versendet</option>
                    <option value="completed">✅ Abgeschlossen</option>
                    <option value="cancelled">❌ Storniert</option>
                    <option value="refunded">💰 Erstattet</option>
                    <option value="failed">⚠️ Fehlgeschlagen</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
      />
    </div>
  );
}

export default OrderDashboard;